/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { api } from "../api";
import { useAgents } from "../hooks/useAgents";

function MetricCard({ icon, label, value }) {
  return (
    <div className="bg-white dark:bg-slate-900/50 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-5">
      <div className="flex items-center gap-2 mb-3">
        {icon && (
          <div className="bg-primary/10 p-2 rounded-lg">
            <span className="material-symbols-outlined text-primary text-base">{icon}</span>
          </div>
        )}
        <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
          {label}
        </span>
      </div>
      <p className="text-2xl font-bold text-slate-900 dark:text-white">
        {value ?? "—"}
      </p>
    </div>
  );
}

function Dashboard() {
  const { isLoading, isAuthenticated, token, user, email, roles, permissions } = useAuth() || {};
  const { agents, setAgents } = useAgents();
  const auth = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [userMetrics, setUserMetrics] = useState(null);
  const [tenantInfo, setTenantInfo] = useState({ logo_url: "", name: "" });

  useEffect(() => {
    if (!isLoading && isAuthenticated && token) {
      const fetchDashboardData = async () => {
        try {
          const headers = { Authorization: `Bearer ${token}` };

          const [metricsRes, userMetricsRes, tenantRes] = await Promise.all([
            api.get("/api/admin/metrics", { headers }),
            api.get("/api/admin/user-metrics", { headers }),
            api.get("/api/admin/metrics", { headers }),
          ]);

          setMetrics(metricsRes.data);
          setUserMetrics(userMetricsRes.data);
          setTenantInfo(tenantRes.data);
        } catch (err) {
          console.error("Error loading dashboard data:", err);
        }
      };

      fetchDashboardData();
    }
  }, [isLoading, isAuthenticated, token]);

  const toggleAgent = async (agentId, enabled) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const payload = [{ key: `agent.${agentId}`, value: enabled ? "true" : "false" }];

      setAgents(prev => prev.map(a => (a.id === agentId ? { ...a, enabled } : a)));

      await api.post("/api/admin/settings", payload, { headers });
    } catch (err) {
      console.error("Error toggling agent setting:", err);
      setAgents(prev => prev.map(a => (a.id === agentId ? { ...a, enabled: !enabled } : a)));
    }
  };

  if (isLoading) {
    return (
      <div className="bg-background-light dark:bg-background-dark font-display min-h-screen flex items-center justify-center">
        <div className="size-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !token) {
    return (
      <div className="bg-background-light dark:bg-background-dark font-display min-h-screen flex items-center justify-center px-4">
        <div className="bg-white dark:bg-slate-900/50 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-8 max-w-sm w-full text-center">
          <div className="bg-primary/10 p-3 rounded-lg inline-flex mb-4">
            <span className="material-symbols-outlined text-primary text-4xl">lock</span>
          </div>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Please log in to view this dashboard.
          </p>
        </div>
      </div>
    );
  }

  const allowedPermissions = ["can_view_admin_stats", "can_manage_agents"];
  const allowedRoles = ["admin", "reviewer"];

  const hasAccess =
    (Array.isArray(roles) && roles.some(r => allowedRoles.includes(r))) ||
    (Array.isArray(permissions) && permissions.some(p => allowedPermissions.includes(p)));

  console.log("Auth Debug Info", { email, user, isAuthenticated, token, role: auth?.role, roles: auth?.roles, permissions: auth?.permissions, hasAccess });

  if (!hasAccess) {
    return (
      <div className="bg-background-light dark:bg-background-dark font-display min-h-screen flex items-center justify-center px-4">
        <div className="bg-white dark:bg-slate-900/50 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-8 max-w-sm w-full text-center">
          <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg inline-flex mb-4">
            <span className="material-symbols-outlined text-red-500 text-4xl">block</span>
          </div>
          <p className="text-sm font-medium text-red-600 dark:text-red-400">
            You do not have access to this dashboard.
          </p>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="bg-background-light dark:bg-background-dark font-display min-h-screen flex items-center justify-center">
        <div className="size-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-slate-100 min-h-screen px-4 py-8 sm:py-10">
      <div className="w-full max-w-7xl mx-auto">

        {/* Page header */}
        <div className="flex flex-col items-center mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-primary/10 p-3 rounded-lg">
              <span className="material-symbols-outlined text-primary text-4xl">dashboard</span>
            </div>
            {tenantInfo.logo_url && (
              <img src={tenantInfo.logo_url} alt="Tenant Logo" className="h-10 rounded-lg" />
            )}
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            Admin Dashboard
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">{email}</p>
        </div>

        {/* Platform Metrics */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Platform Metrics</span>
            <div className="flex-1 border-t border-slate-100 dark:border-slate-800" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <MetricCard icon="group" label="Active Agents" value={metrics.active_agents} />
            <MetricCard icon="trending_up" label="Total Sessions" value={metrics.total_sessions} />
            <MetricCard icon="error_outline" label="Errors Logged" value={metrics.error_count} />
          </div>
        </div>

        {/* User Submission Metrics */}
        {userMetrics && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">User Submission Metrics</span>
              <div className="flex-1 border-t border-slate-100 dark:border-slate-800" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                ["Total Submissions", userMetrics.total_submissions],
                ["Last 24h", userMetrics.submissions_last_24h],
                ["Pending Reviews", userMetrics.pending_reviews],
                ["Approved", userMetrics.approved_reviews],
                ["Rejected", userMetrics.rejected_reviews],
                ["Parsed OK", userMetrics.parsed_success],
                ["Parsing Errors", userMetrics.parsed_error],
                ["Voice Only", userMetrics.voice_only],
                ["Photo Only", userMetrics.photo_only],
                ["Voice + Photo", userMetrics.voice_and_photo],
              ].map(([label, value]) => (
                <MetricCard key={label} label={label} value={value} />
              ))}
            </div>
          </div>
        )}

        {/* Agent Management */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Agent Management</span>
            <div className="flex-1 border-t border-slate-100 dark:border-slate-800" />
          </div>

          <div className="space-y-3">
            {agents?.map((agent) => (
              <div
                key={agent.id}
                className="bg-white dark:bg-slate-900/50 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 px-5 py-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-lg">
                    <span className="material-symbols-outlined text-primary text-base">smart_toy</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{agent.name}</p>
                    <span className={`text-[11px] font-semibold uppercase tracking-wide ${agent.enabled ? "text-emerald-600 dark:text-emerald-400" : "text-slate-400 dark:text-slate-500"}`}>
                      {agent.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </div>

                {/* Toggle */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={agent.enabled}
                  onClick={() => toggleAgent(agent.id, !agent.enabled)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                    agent.enabled ? "bg-primary" : "bg-slate-200 dark:bg-slate-700"
                  }`}
                >
                  <span
                    className={`inline-block size-5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${
                      agent.enabled ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Info notice */}
        <div className="mt-8 bg-white dark:bg-slate-900/50 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-4">
          <div className="flex items-start gap-3 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg">
            <span className="material-symbols-outlined text-primary text-sm mt-0.5">info</span>
            <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
              Agent toggles take effect immediately. Metrics refresh on page load. Role-based access is enforced server-side.
            </p>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-slate-400 dark:text-slate-500">© 2025 Fluxonix Security. All rights reserved.</p>
        </div>
      </div>

      {/* Background glow */}
      <div className="fixed inset-x-0 bottom-0 -z-10 h-56 sm:h-64 pointer-events-none opacity-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
        <div className="absolute -bottom-28 -left-28 w-80 h-80 sm:w-96 sm:h-96 bg-primary rounded-full blur-[120px]"></div>
        <div className="absolute -bottom-28 -right-28 w-80 h-80 sm:w-96 sm:h-96 bg-primary rounded-full blur-[120px]"></div>
      </div>
    </div>
  );
}

export default Dashboard;